import { createFileRoute, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Accordion,
  ActionIcon,
  Anchor,
  Badge,
  Card,
  Group,
  Indicator,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconBook,
  IconBrandGithub,
  IconExternalLink,
  IconHeartHandshake,
  IconHelp,
  IconHistory,
  IconLifebuoy,
  IconMail,
  IconSearch,
  IconShieldLock,
  IconTicket,
  IconX,
} from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { EmptyState } from '@/shared/components/EmptyState';

export const Route = createFileRoute('/_app/help')({ component: HelpPage });

interface QuickLink {
  icon: typeof IconBook;
  label: string;
  description: string;
  color: string;
  href?: string;
  to?: string;
  external?: boolean;
}

const QUICK_LINKS: QuickLink[] = [
  {
    icon: IconBook,
    label: 'Guida utente',
    description: 'Manuale operativo per gli utenti finali.',
    color: 'blue',
    href: 'https://docs.maestrale.it/maefwk10',
    external: true,
  },
  {
    icon: IconLifebuoy,
    label: 'Centro assistenza',
    description: 'Apri e segui i tuoi ticket di supporto.',
    color: 'teal',
    to: '/tickets',
  },
  {
    icon: IconMail,
    label: 'Contattaci',
    description: 'supporto@maestrale.it · risposta entro 24h lav.',
    color: 'grape',
    href: 'mailto:supporto@maestrale.it',
  },
  {
    icon: IconShieldLock,
    label: 'Privacy & GDPR',
    description: 'Informativa, consensi, esportazione dati.',
    color: 'orange',
    to: '/legals',
  },
  {
    icon: IconBrandGithub,
    label: 'Changelog rilasci',
    description: 'Le novità di ogni versione.',
    color: 'gray',
    to: '/changelog',
  },
  {
    icon: IconHistory,
    label: 'Audit personale',
    description: 'Cronologia dei tuoi accessi e azioni.',
    color: 'indigo',
    to: '/user-audit',
  },
];

interface Faq {
  q: string;
  a: string;
  tags: string[];
}

const FAQS: Faq[] = [
  {
    q: 'Come compilo il timesheet della settimana?',
    a: 'Dal menu Timesheet, scegli la settimana, clicca sulla cella giorno/progetto e inserisci le ore. Le ore straordinarie vanno nel campo dedicato. Premi Salva o invio: la riga si chiude automaticamente.',
    tags: ['timesheet', 'ore', 'rendicontazione'],
  },
  {
    q: 'Come richiedo un giorno di smart working?',
    a: 'Vai su Smart working → Richiedi giornata. Seleziona la data e l\'eventuale fascia oraria, scegli l\'approvatore. La richiesta resta in coda finché il manager non la approva (riceverai notifica).',
    tags: ['smart working', 'sw', 'remote'],
  },
  {
    q: 'Come prenoto un\'auto aziendale?',
    a: 'Apri "Prenota mezzo" dall\'home oppure il menu Bookings → Strumenti calendar. Trascina sulla griglia per selezionare l\'intervallo, scegli il veicolo. Vedrai i conflitti in tempo reale.',
    tags: ['auto', 'veicolo', 'prenotazione', 'mezzo'],
  },
  {
    q: 'Come reset/cambio la password?',
    a: 'Dal login, clicca "Password dimenticata". Riceverai una mail con il link per impostarne una nuova. Se sei già loggato, vai su Impostazioni → Sicurezza.',
    tags: ['password', 'login', 'reset', 'accesso'],
  },
  {
    q: 'Cosa significa "stato fatturazione" sul timesheet?',
    a: 'Il badge indica se la voce è fatturabile, già fatturata, in revisione o non fatturabile. È determinato da regole sulla commessa associata.',
    tags: ['fatturazione', 'billing', 'timesheet'],
  },
  {
    q: 'Le notifiche push non arrivano sul telefono.',
    a: 'Verifica i permessi notifiche dall\'OS, poi in Impostazioni → Notifiche assicurati che il canale sia attivo. Su Android alcuni produttori (Xiaomi, Huawei) richiedono autorizzazione battery exception.',
    tags: ['notifiche', 'push', 'mobile', 'android', 'ios'],
  },
  {
    q: 'Posso esportare i dati di un report?',
    a: 'Sì: nella pagina Reporting scegli il tipo di report, il periodo e usa i bottoni Excel/PDF/CSV. Anteprima fino a 100 righe; export completo.',
    tags: ['export', 'excel', 'pdf', 'csv', 'reporting'],
  },
  {
    q: 'Come funzionano i ruoli e i permessi?',
    a: 'Ogni utente è assegnato a uno o più ruoli, che combinano permessi su risorse e azioni. Solo gli amministratori del tenant possono modificare ruoli da Settings → Ruoli.',
    tags: ['ruoli', 'permessi', 'sicurezza', 'casl'],
  },
];

function HelpPage() {
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 200);

  const fuse = useMemo(
    () => new Fuse(FAQS, { keys: ['q', 'a', 'tags'], threshold: 0.35, includeScore: false }),
    []
  );

  const filteredFaqs = useMemo(() => {
    if (!debounced.trim()) return FAQS;
    return fuse.search(debounced).map((r) => r.item);
  }, [debounced, fuse]);

  const filteredLinks = useMemo(() => {
    if (!debounced.trim()) return QUICK_LINKS;
    const q = debounced.toLowerCase();
    return QUICK_LINKS.filter(
      (l) => l.label.toLowerCase().includes(q) || l.description.toLowerCase().includes(q)
    );
  }, [debounced]);

  const openTicketForCurrentPage = () => {
    try { sessionStorage.setItem('help-ticket-from', currentPath); } catch { /* ignore */ }
    void navigate({ to: '/tickets' });
  };

  return (
    <Stack maw={960}>
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={1}>
            <IconHelp size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Aiuto
          </Title>
          <Text c="dimmed">Risorse di supporto, FAQ e canali di contatto.</Text>
        </div>
        <ServiceStatusBadge />
      </Group>

      <TextInput
        placeholder="Cerca tra FAQ e risorse (es. timesheet, ferie, password)…"
        leftSection={<IconSearch size={16} />}
        rightSection={
          query ? (
            <ActionIcon variant="subtle" onClick={() => setQuery('')} aria-label="Cancella">
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        size="md"
      />

      {filteredLinks.length > 0 && (
        <>
          <Text fw={600} mt="sm">Risorse rapide</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {filteredLinks.map((l) => {
              const card = (
                <Card withBorder padding="lg" radius="md" h="100%" style={{ cursor: 'pointer' }}>
                  <Group wrap="nowrap" align="flex-start">
                    <ThemeIcon size="xl" radius="md" variant="light" color={l.color}>
                      <l.icon size={24} />
                    </ThemeIcon>
                    <div style={{ flex: 1 }}>
                      <Group gap={4} wrap="nowrap">
                        <Text fw={600}>{l.label}</Text>
                        {l.external && <IconExternalLink size={12} style={{ opacity: 0.5 }} />}
                      </Group>
                      <Text size="xs" c="dimmed" mt={2} lineClamp={2}>{l.description}</Text>
                    </div>
                  </Group>
                </Card>
              );
              return l.to ? (
                <Link key={l.label} to={l.to} style={{ textDecoration: 'none' }}>{card}</Link>
              ) : (
                <Anchor
                  key={l.label}
                  href={l.href}
                  target={l.external ? '_blank' : undefined}
                  rel={l.external ? 'noopener noreferrer' : undefined}
                  underline="never"
                >
                  {card}
                </Anchor>
              );
            })}
          </SimpleGrid>
        </>
      )}

      <Card withBorder padding="lg" radius="md" mt="md">
        <Group justify="space-between" mb="sm">
          <Group gap={8}>
            <ThemeIcon variant="light" color="teal" radius="md" size="md">
              <IconHeartHandshake size={18} />
            </ThemeIcon>
            <Text fw={600}>Domande frequenti</Text>
            <Badge variant="light">{filteredFaqs.length}</Badge>
          </Group>
          <Tooltip label="Apri un ticket riferito alla pagina precedente">
            <ActionIcon variant="light" color="blue" onClick={openTicketForCurrentPage} aria-label="Apri ticket">
              <IconTicket size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
        {filteredFaqs.length === 0 ? (
          <EmptyState
            title="Nessuna FAQ corrisponde"
            description={<>Non abbiamo risposte per "<strong>{debounced}</strong>". Apri un ticket di supporto per ricevere assistenza diretta.</>}
            actionLabel="Apri un ticket"
            onAction={openTicketForCurrentPage}
            py="lg"
          />
        ) : (
          <ScrollArea.Autosize mah={520}>
            <Accordion variant="separated">
              {filteredFaqs.map((f) => (
                <Accordion.Item key={f.q} value={f.q}>
                  <Accordion.Control>{f.q}</Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm">{f.a}</Text>
                    <Group gap={4} mt="xs">
                      {f.tags.map((t) => (
                        <Badge key={t} size="xs" variant="dot" color="gray">{t}</Badge>
                      ))}
                    </Group>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </ScrollArea.Autosize>
        )}
      </Card>
    </Stack>
  );
}

function ServiceStatusBadge() {
  return (
    <Anchor href="https://status.maestrale.it" target="_blank" rel="noopener noreferrer" underline="never">
      <Indicator inline color="green" size={10} processing position="middle-start" offset={-2}>
        <Badge variant="light" color="green" leftSection={null} pl="md">
          Stato servizio
        </Badge>
      </Indicator>
    </Anchor>
  );
}
